/** @jsxImportSource theme-ui */
import { Box, Flex, Grid } from 'theme-ui';
import { useState } from 'react';
import { Formik, FormikProps } from 'formik';
import qs from 'qs';
import { nanoid } from 'nanoid';
import { useSearchParams } from 'react-router-dom';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import { useGetCompaniesQuery } from '@/store/apiService';
import { VossLicenseAuditDecisionAction, VossLicenseAuditFindingKind } from '@/types/types';

export type SubscriptionAuditFilterValues = {
    Kind: string;
    Decision: string;
    CompanyId: string;
    ShowReportOnly: boolean;
};

export const kindLabels: Record<VossLicenseAuditFindingKind, string> = {
    TierMismatch: 'Tier mismatch',
    SubscriptionMissing: 'Subscription missing in Voss',
    CustomerMissing: 'Customer missing in Voss',
    SubscriptionTerminated: 'Subscription terminated in Voss',
    AddOnTerminated: 'Add-on terminated in Voss',
    AddOnQuantityMismatch: 'Add-on quantity differs',
    AddOnMissingInVoss: 'Add-on missing in Voss',
    FailedToCheck: 'Failed to check',
    SubscriptionReplaced: 'Subscription replaced in Voss',
    AddOnMissingLocally: 'Add-on missing in BokaMera',
    ValidToMismatch: 'License end date differs from Voss',
    PlanMismatch: 'Billing plan differs from Voss',
    DuplicateActiveLicense: 'Duplicate active license',
    BillingInformationChange: 'Billing information updated from Voss',
    ChangeLogRowUnresolved: 'Voss change could not be matched',
    FreeNoSubscription: 'Free tier, nothing in Voss',
};

export const kindDescriptions: Record<VossLicenseAuditFindingKind, string> = {
    TierMismatch:
        'Voss bills a different tier than the local license. The local license is moved to the Voss tier.',
    SubscriptionMissing:
        'Voss has no active subscription for this company although a paid local license is active. The company is not being billed.',
    CustomerMissing:
        'Voss has no customer for this company although a paid local license is active. The company is not being billed.',
    SubscriptionTerminated:
        'The subscription the local licenses point at is terminated in Voss and nothing replaced it. The local licenses are ended.',
    AddOnTerminated: 'The add-on line is terminated in Voss. The local add-on license is ended.',
    AddOnQuantityMismatch:
        'Voss bills a different quantity of this add-on than BokaMera has rows for. Voss tracks only a number, so a person picks the rows.',
    AddOnMissingInVoss:
        'A local add-on license is active but Voss has no active line for it. The add-on is not being billed.',
    FailedToCheck: 'Voss could not be read for this company this run. Nothing was decided.',
    SubscriptionReplaced:
        'The subscription the local licenses point at is terminated in Voss but one active replacement exists. The licenses are relinked to it.',
    AddOnMissingLocally:
        'Voss bills an add-on line that has no local license. API and code-lock rows are created; domain and display rows need a person.',
    ValidToMismatch:
        'The license end date is set from the Voss subscription or line: its termination date, or open-ended when there is none.',
    PlanMismatch:
        'Voss has the subscription on a different billing plan than the local license carries. The Voss plan is stamped locally.',
    DuplicateActiveLicense:
        'One subscription has more than one active application license locally. The one matching the Voss tier is kept, the others are ended.',
    BillingInformationChange:
        'The nightly change-log job will copy these billing fields from the Voss customer into BokaMera.',
    ChangeLogRowUnresolved:
        'Voss reported a change that the nightly jobs cannot attach to an auditable company. A person must look.',
    FreeNoSubscription:
        'The company only has the Free license and Voss has no customer or subscription for it. Nothing to bill, nothing is changed; listed for information.',
};

export const decisionLabels: Record<VossLicenseAuditDecisionAction, string> = {
    Applied: 'Applied',
    WouldApply: 'Would apply',
    SkippedLocalChangePending: 'Skipped – local change pending',
    SkippedPendingScheduledChange: 'Skipped – scheduled change',
    SkippedCap: 'Skipped – cap',
    ReportOnly: 'Report only',
};

export const decisionDescriptions: Record<VossLicenseAuditDecisionAction, string> = {
    Applied:
        'BokaMera was changed to match Voss (tier moved, or licenses ended). Voss itself is never changed.',
    WouldApply: 'Preview run: this change would have been made by a real run. Nothing was changed.',
    SkippedLocalChangePending:
        'Not changed: the license was changed in BokaMera within the last day, or a change to Voss has failed and is unresolved. Voss may not have caught up yet, so it is checked again on the next run.',
    SkippedPendingScheduledChange:
        'Not changed: Voss has a scheduled change (for example a downgrade at period end) that has not taken effect yet.',
    SkippedCap:
        'Not changed: the run had already reached its limit on changes. Raise the limit or run again.',
    ReportOnly:
        'Nothing was changed. The difference needs a person to resolve it, and an entry was added to the Voss error log.',
};

export const defaultFilterValues = (): SubscriptionAuditFilterValues => ({
    Kind: '',
    Decision: '',
    CompanyId: '',
    ShowReportOnly: false,
});

const valuesFromSearch = (search: string): SubscriptionAuditFilterValues => {
    const defaults = defaultFilterValues();
    const parsed = qs.parse(search) as Record<string, string | undefined>;
    const text = (key: 'Kind' | 'Decision' | 'CompanyId') =>
        typeof parsed[key] === 'string' ? (parsed[key] as string) : defaults[key];
    return {
        Kind: text('Kind'),
        Decision: text('Decision'),
        CompanyId: text('CompanyId'),
        ShowReportOnly: parsed.ShowReportOnly === 'true',
    };
};

const SubscriptionAuditFilters: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [companySearch, setCompanySearch] = useState('');
    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });

    return (
        <Formik
            initialValues={valuesFromSearch(searchParams.toString())}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
                const runId = searchParams.get('RunId');
                setSearchParams(
                    qs.stringify({ ...(runId ? { RunId: runId } : {}), ...values, rand: nanoid(6) })
                );
                setSubmitting(false);
            }}
        >
            {(props: FormikProps<SubscriptionAuditFilterValues>) => (
                <>
                    <Grid
                        sx={{
                            '@media (width > 700px)': {
                                gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))',
                            },
                        }}
                    >
                        <GrayFormikField
                            name="Kind"
                            type="select"
                            label="Kind"
                            options={[
                                { value: '', label: 'All kinds' },
                                ...Object.entries(kindLabels).map(([value, label]) => ({
                                    value,
                                    label,
                                })),
                            ]}
                        />
                        <GrayFormikField
                            name="Decision"
                            type="select"
                            label="Decision"
                            options={[
                                { value: '', label: 'All decisions' },
                                ...Object.entries(decisionLabels).map(([value, label]) => ({
                                    value,
                                    label,
                                })),
                            ]}
                        />
                        <Box variant="form-field">
                            <GrayFormikField
                                name="CompanyId"
                                value={props.values.CompanyId}
                                type="dropdownsearch"
                                label="Company (optional)"
                                placeholder="All companies"
                                onSearchChange={(val: string) => setCompanySearch(val)}
                                onSelect={(item: any) =>
                                    props.setFieldValue('CompanyId', item?.value ?? '')
                                }
                                items={companies.data?.Results?.map((company: any) => ({
                                    value: company.Id,
                                    label: `${company.Name} (${company.Email})`,
                                }))}
                                isLoading={companies.isFetching}
                                total={companies.data?.Total}
                            />
                        </Box>
                    </Grid>
                    <Flex
                        p="2"
                        sx={{
                            width: '100%',
                            gap: 2,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            title="Report-only findings never change anything and can run into the thousands, so they are hidden unless a decision filter is set or this is ticked."
                            sx={{ '& label': { mb: 0 } }}
                        >
                            <GrayFormikField
                                type="checkbox"
                                name="ShowReportOnly"
                                label="Show report-only findings"
                                disabled={!!props.values.Decision}
                            />
                        </Box>
                        <Flex sx={{ gap: 2 }}>
                            <Button
                                variant="secondary"
                                type="button"
                                title="Only the changes a real run makes from this state (decision: Would apply)"
                                onClick={() => {
                                    const values = {
                                        ...defaultFilterValues(),
                                        Decision: 'WouldApply',
                                    };
                                    props.resetForm({ values });
                                    const runId = searchParams.get('RunId');
                                    setSearchParams(
                                        qs.stringify({
                                            ...(runId ? { RunId: runId } : {}),
                                            ...values,
                                            rand: nanoid(6),
                                        })
                                    );
                                }}
                            >
                                Tonight
                            </Button>
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    props.resetForm({ values: defaultFilterValues() });
                                    const runId = searchParams.get('RunId');
                                    setSearchParams(runId ? qs.stringify({ RunId: runId }) : '');
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                isLoading={props.isSubmitting}
                                disabled={props.isSubmitting}
                                type="submit"
                                onClick={props.submitForm}
                            >
                                Filter
                            </Button>
                        </Flex>
                    </Flex>
                </>
            )}
        </Formik>
    );
};

export default SubscriptionAuditFilters;
